<script setup>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import { TEMPLATE_CATEGORIES, templatesByCategory } from '../templates';

const emit = defineEmits(['select']);
const { t } = useI18n();

const dialogRef = ref(null);

const open = () => dialogRef.value?.open();
const close = () => dialogRef.value?.close();

const choose = templateId => {
  emit('select', templateId);
  close();
};

// Sem chave dinâmica de i18n de propósito: são dois grupos fixos e o linter
// (e o Crowdin) enxergam as duas chaves.
const groupLabel = category =>
  category === 'segment'
    ? t('FORMS_MGMT.TEMPLATES.GROUPS.SEGMENT')
    : t('FORMS_MGMT.TEMPLATES.GROUPS.GENERAL');

const groupHint = category =>
  category === 'segment'
    ? t('FORMS_MGMT.TEMPLATES.GROUPS.SEGMENT_HINT')
    : t('FORMS_MGMT.TEMPLATES.GROUPS.GENERAL_HINT');

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    type="edit"
    width="3xl"
    :title="t('FORMS_MGMT.TEMPLATES.TITLE')"
    :description="t('FORMS_MGMT.TEMPLATES.DESCRIPTION')"
    :show-cancel-button="false"
    :show-confirm-button="false"
  >
    <div class="flex flex-col gap-6">
      <div
        v-for="category in TEMPLATE_CATEGORIES"
        :key="category"
        class="flex flex-col gap-3"
      >
        <div class="flex flex-col gap-0.5">
          <h3 class="text-sm font-semibold text-n-slate-12">
            {{ groupLabel(category) }}
          </h3>
          <p class="text-xs text-n-slate-11">
            {{ groupHint(category) }}
          </p>
        </div>

        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <button
            v-if="category === 'general'"
            type="button"
            class="relative p-5 rounded-xl border-2 border-dashed border-n-weak hover:border-n-slate-7 bg-n-solid-1 transition-colors text-left cursor-pointer"
            @click="choose(null)"
          >
            <div
              class="w-11 h-11 rounded-xl bg-n-alpha-2 flex items-center justify-center mb-4"
            >
              <span class="i-lucide-plus w-5 h-5 text-n-slate-11" />
            </div>
            <h4 class="text-base font-semibold text-n-slate-12 mb-1">
              {{ t('FORMS_MGMT.TEMPLATES.BLANK.NAME') }}
            </h4>
            <p class="text-sm text-n-slate-11">
              {{ t('FORMS_MGMT.TEMPLATES.BLANK.DESCRIPTION') }}
            </p>
          </button>

          <button
            v-for="template in templatesByCategory(category)"
            :key="template.id"
            type="button"
            class="relative p-5 rounded-xl border-2 border-n-weak hover:border-n-slate-7 bg-n-solid-1 transition-colors text-left cursor-pointer"
            @click="choose(template.id)"
          >
            <div
              class="w-11 h-11 rounded-xl flex items-center justify-center mb-4"
              :style="{ backgroundColor: `${template.color}1F` }"
            >
              <span
                class="w-5 h-5"
                :class="template.icon"
                :style="{ color: template.color }"
              />
            </div>
            <h4 class="text-base font-semibold text-n-slate-12 mb-1">
              {{ template.name }}
            </h4>
            <p class="text-sm text-n-slate-11 line-clamp-2">
              {{ template.description }}
            </p>
            <div
              class="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs text-n-slate-10"
            >
              <span class="flex items-center gap-1.5">
                <span class="i-lucide-list-checks w-3.5 h-3.5" />
                {{
                  t('FORMS_MGMT.TEMPLATES.FIELD_COUNT', {
                    count: template.fields.length,
                  })
                }}
              </span>
              <span v-if="template.scoring" class="flex items-center gap-1.5">
                <span class="i-lucide-gauge w-3.5 h-3.5" />
                {{ t('FORMS_MGMT.TEMPLATES.WITH_SCORING') }}
              </span>
              <span
                v-if="template.fields.some(item => item.visible_if)"
                class="flex items-center gap-1.5"
              >
                <span class="i-lucide-git-branch w-3.5 h-3.5" />
                {{ t('FORMS_MGMT.TEMPLATES.WITH_CONDITIONS') }}
              </span>
            </div>
          </button>
        </div>
      </div>
    </div>
  </Dialog>
</template>
